import { DatePipe } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { ApplicationConfig, importProvidersFrom } from "@angular/core";
import { BrowserAnimationsModule, provideAnimations } from "@angular/platform-browser/animations";
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
  withViewTransitions,
} from "@angular/router";

import { environment } from "src/environments/environment";

import { MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldDefaultOptions,
} from "@angular/material/form-field";
import { MatSnackBarModule } from "@angular/material/snack-bar";

import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { MultiTranslateHttpLoader } from "ngx-translate-multi-http-loader";

import { AuthService } from "./core/services/auth.service";
import { LocalStorageService } from "./core/services/local-storage.service";
import { ObservableService } from "./core/services/observable.service";
import { SessionStorageService } from "./core/services/session-storage.service";

import { appRoutes as APP_ROUTES } from "./app.routes";

import { STEPPER_GLOBAL_OPTIONS } from "@angular/cdk/stepper";
import { MAT_DIALOG_DEFAULT_OPTIONS } from "@angular/material/dialog";
import { ApiInterceptor } from "./core/interceptors/api.interceptor";
import { AuthInterceptor } from "./core/interceptors/auth.interceptor";
import { ErrorInterceptor } from "./core/interceptors/error.interceptor";
import { InternetInterceptor } from "./core/interceptors/internet.interceptor";
import { LoaderInterceptor } from "./core/interceptors/loader.interceptor";
import { provideCharts, withDefaultRegisterables } from "ng2-charts";

export function createTranslateLoader(http: HttpBackend): MultiTranslateHttpLoader {
  return new MultiTranslateHttpLoader(http, [{ prefix: environment.ASSET, suffix: ".json" }]);
}

const matFormFieldDefaultOptions: MatFormFieldDefaultOptions = {
  appearance: "outline",
  subscriptSizing: "dynamic",
};

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom([BrowserAnimationsModule]),
    importProvidersFrom(MatSnackBarModule),
    provideAnimations(),

    SessionStorageService,
    LocalStorageService,
    ObservableService,
    AuthService,

    provideRouter(
      APP_ROUTES,
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: "always" }),
      withViewTransitions(),
    ),
    { provide: HTTP_INTERCEPTORS, useClass: InternetInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiInterceptor, multi: true },
    provideHttpClient(withInterceptors([LoaderInterceptor]), withInterceptorsFromDi()),
    provideCharts(withDefaultRegisterables()),
    importProvidersFrom(
      MatDatepickerModule,
      MatNativeDateModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpBackend],
        },
      }),
    ),

    DatePipe,

    // Global material tokens
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: matFormFieldDefaultOptions },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        autoFocus: false,
      },
    },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false, showError: true },
    },
  ],
};
